import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { SidebarCollapsibleSection } from 'components/blocks/layout/sidebar-section';
import { SidebarTreeItem } from 'components/blocks/layout/sidebar-items';
import { useSidebar } from 'components/ui/sidebar';
import { getConversationDisplayTitle } from 'utils/chat-utilities';
import { Draggable } from 'features/sidebar/Draggable';

interface IndividualChatsSectionProps {
    conversations: any[];
    isSidebarLoading: boolean;
    renderRightAction: (id: string, title: string) => React.ReactNode;
    variant?: 'default' | 'figma';
}

export const IndividualChatsSection = ({
    conversations,
    isSidebarLoading,
    renderRightAction,
    variant = 'figma',
}: IndividualChatsSectionProps) => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { isMobile, setOpenMobile } = useSidebar();
    const navigate = useNavigate();

    const chatList = conversations ?? [];

    return (
        <SidebarCollapsibleSection
            title={t("INDIVIDUAL_CHATS")}
            variant={variant}
        >
            <div className="flex flex-col gap-1">
                {chatList.length === 0 && !isSidebarLoading && (
                    <div className="py-1 px-2 text-center">
                        <p className="text-xs text-muted-foreground italic mb-2">
                            {t('NO_INDIVIDUAL_CHAT_YET') || 'No individual chats yet'}
                        </p>
                    </div>
                )}
                {chatList.map((item: any) => {
                    const displayTitle = getConversationDisplayTitle(
                        item.ConversationTitle,
                        t('NEW_CHAT')
                    );

                    return (
                    <Draggable key={item.ConversationId} id={item.ConversationId}>
                        <SidebarTreeItem
                            label={displayTitle}
                            variant={variant}
                            isActive={pathname.includes(item.ConversationId)}
                            onClick={() => {
                                if (isMobile) setOpenMobile(false);
                                navigate(`/chat/${item.ConversationId}`);
                            }}
                            rightAction={renderRightAction(item.ConversationId, displayTitle)}
                        />
                    </Draggable>
                    );
                })}
                {isSidebarLoading && (
                    <div className="flex h-10 items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-[#888888] dark:text-grey-400" />
                    </div>
                )}
            </div>
        </SidebarCollapsibleSection>
    );
};
